import { trace } from "./utils";
import DefaultListener from "./DefaultListener";
import SoundControl,{ initSounds } from "./SoundControl";
import MicroSIPDefault, { ensureMicroSIP } from "./MicroSIP-shim";
const MicroSIP: any = MicroSIPDefault as any;
// import "./MicroSIP";

declare const ConfigurationLoader: any; // Assuming ConfigurationLoader is a global object

export enum SessionStatus {
    ESTABLISHED = "ESTABLISHED",
    REGISTERED = "REGISTERED",
    DISCONNECTED = "DISCONNECTED",
    FAILED = "FAILED",
    INCOMING_CALL = "INCOMING_CALL",
    UNREGISTERED = "UNREGISTERED",
    AUTHENTICATION_FAIL = "AUTHENTICATION_FAIL"
}

export enum CallStatus {
    RING = "RING",
    ESTABLISHED = "ESTABLISHED",
    HOLD = "HOLD",
    FINISH = "FINISH",
    FAILED = "FAILED",
    PENDING = "PENDING",
    RING_MEDIA = "RING_MEDIA",
    BUSY = "BUSY",
    SESSION_PROGRESS = "SESSION_PROGRESS"
}

export interface SipOptions {
    login: String|Number;
    password?: String;
    authenticationName?: String|Number;
    domain: String;
    outboundProxy?: String;
    port?: Number;
    useProxy: boolean;
    registerRequired: boolean;
}

export interface Call {
    id(): string;
    status(): CallStatus;
    answer(options: { localVideoDisplay: HTMLElement | null; remoteVideoDisplay: HTMLElement | null; }): void;
    hangup(): void;
    sendDTMF(dtmf: string): void;
    hold(): void;
    holdForTransfer(): void;
    unhold(): void;
    transfer(target: string): void;
    getStats(callback: (statistics: any) => void): void;
    caller(): string;
    callee(): string;
    on(event: CallStatus, callback: (call: Call) => void): Call;
    call(): void;
    muteAudio(): void;
    unmuteAudio(): void;
    isAudioMuted(): boolean;
}

export interface Session {
    createCall(options: {
        callee: String;
        visibleName: String | Number;
        localVideoDisplay: HTMLElement;
        remoteVideoDisplay: HTMLElement;
        constraints: { audio: boolean; video: boolean; };
    }): Call;
    disconnect(): void;
    on(event: SessionStatus, callback: (session: Session) => void): Session;
}

interface Params {
    credentials:{
        extension : Number;
        password : String;
        host : String;
        port : Number;
        ws : String;
    }
    config?:{
        dom?:{
            localVideo:HTMLElement;
            remoteVideo:HTMLElement;
        }
    }
}

interface Credentials {
    extension : Number;
    password : String;
    host : String;
    port : Number;
    ws : String;
}

interface Callback {
    [key: string]: (e: any) => any;
}

class Phoner {
    private credentials: Credentials;
    private microsipListener: DefaultListener | null;
    public currentCall: Call | null;
    private session: Session | null;
    private holdedCall: Call | null;
    private incomingCall: boolean;
    private localVideo: HTMLElement;
    private remoteVideo: HTMLElement;
    private sipOptions: SipOptions | null;
    private timerInterval: any; // NodeJS.Timeout | null;
    private start: number;
    private min: number;
    private activities = (actName:string, payload:any)=>{ return {event:actName, payload}  }

    private callback: Callback = {
                                    "connectionStatus": (e: any) => { return e },
                                    "registrationStatus": (e: any) => { return e },
                                    "callListen": (e: any) => { return e },
                                    "callStatusListener": (e: any) => { return e },
                                    "dialEstablished": (e: any) => { return e },
                                    "unregistered": (e: any) => { return e },
                                    "info": (e: any) => { return e },
                                };

    constructor(params:Params) {
    
        // console.log('params.config?.dom?.localVideo ', params.config?.dom?.localVideo );
        

        this.credentials = params?.credentials ||{};
        this.microsipListener = null;
        this.currentCall = null;
        this.session = null;
        this.holdedCall = null;
        this.incomingCall = false;
        this.localVideo = params.config?.dom?.localVideo as HTMLElement;
        this.remoteVideo = params.config?.dom?.remoteVideo as HTMLElement;
        this.sipOptions = null;
        this.timerInterval = null;
        this.start = 0;
        this.min = 0;

        try {
            // Ensure the MicroSIP UMD bundle is loaded at runtime, then initialize it.
            ensureMicroSIP().then((ms) => {
                try { ms.init(); } catch (err) { console.error('MicroSIP.init failed:', err); }
            }).catch((err) => {
                console.error('Failed to load MicroSIP:', err);
            });
        } catch(e) {
            console.log('err');
            console.error(e);
        }
    }

    on(event: keyof Callback, handler: (e: any) => any): void {
        this.callback[event] = handler;
    }

    activity(handler:(e:any, payload:any)=>any):void{
        this.activities = handler
    }

    init(): void {
        this.microsipListener = new DefaultListener();
    }

    connect(): void {
        // Ensure MicroSIP is loaded, then perform connect actions.
        ensureMicroSIP().then((ms) => {
            try {
                if (ms.Browser && ms.Browser.isSafariWebRTC && ms.getMediaProviders && ms.Browser.isSafariWebRTC() && ms.getMediaProviders()[0] === "WebRTC") {
                    ms.playFirstVideo(this.localVideo, true);
                    ms.playFirstVideo(this.remoteVideo, false);
                }
            } catch (e) {
                console.warn('MicroSIP browser detection/firstVideo failed', e);
            }

            const me = this;
            const url = this.credentials.ws;

            this.sipOptions = {} as SipOptions;
            this.sipOptions.login = this.credentials.extension;
            this.sipOptions.password = this.credentials.password;
            this.sipOptions.authenticationName = this.credentials.extension;
            this.sipOptions.domain = this.credentials.host;
            this.sipOptions.outboundProxy = this.credentials.host;
            this.sipOptions.port = this.credentials.port;
            this.sipOptions.useProxy = true;
            this.sipOptions.registerRequired = true;

            const connectionOptions = {
                urlServer: url,
                sipOptions: this.sipOptions
            };

            initSounds();
            ms.createSession(connectionOptions)
                .on(ms.constants.SESSION_STATUS.ESTABLISHED, function(session: Session) {
                    me.session = session;
                    me.connectionStatusListener(ms.constants.SESSION_STATUS.ESTABLISHED);
                })
                .on(ms.constants.SESSION_STATUS.REGISTERED, function() {
                    me.registrationStatusListener(ms.constants.SESSION_STATUS.REGISTERED);
                })
                .on(ms.constants.SESSION_STATUS.DISCONNECTED, function() {
                    me.connectionStatusListener(ms.constants.SESSION_STATUS.DISCONNECTED);
                })
                .on(ms.constants.SESSION_STATUS.FAILED, function() {
                    me.connectionStatusListener(ms.constants.SESSION_STATUS.FAILED);
                })
                .on(ms.constants.SESSION_STATUS.INCOMING_CALL, function(call: Call) {
                    call.on(ms.constants.CALL_STATUS.RING, function() {
                        me.callStatusListener(call);
                    }).on(ms.constants.CALL_STATUS.ESTABLISHED, function() {
                        me.callStatusListener(call);
                    }).on(ms.constants.CALL_STATUS.HOLD, function() {
                        me.callStatusListener(call);
                    }).on(ms.constants.CALL_STATUS.FINISH, function() {
                        me.callStatusListener(call);
                        me.incomingCall = false;
                    }).on(ms.constants.CALL_STATUS.FAILED, function() {
                        me.callStatusListener(call);
                        me.incomingCall = false;
                    });
                    me.onCallListener(call);
                });

            trace("Phone - connecting");
            const status = "CONNECTING";
            this.callback.connectionStatus(status);
            const payload = {status};
            this.activities('connectionStatus',{payload});
        }).catch(err => {
            console.error('MicroSIP failed to load in connect():', err);
        });
     }

    disconnect(): void {
        trace("Phone - disconnect");
        if (this.session) {
            this.session.disconnect();
        }
    }

    cancel(): void {
        if (this.currentCall) {
            this.hangup();
        } else {

        }
    }

    call(callee: string, hasVideo: boolean): void {
        const me = this;
        trace("Phone - call " + callee);


        const status = "TRYING";
        this.callback.callStatusListener(status);
        const payload = {status, callNum:callee, callId:null, type:'out'}
        this.activities('callStatus',{payload});

        const constraints = {
            audio: true,
            video: hasVideo
        };

        if (!this.session) {
            trace("Phone - Session not established. Cannot make a call.");
            return;
        }

        const outCall = this.session.createCall({
            callee: callee,
            visibleName: this.sipOptions?.login || '',
            localVideoDisplay: this.localVideo,
            remoteVideoDisplay: this.remoteVideo,
            constraints: constraints
        }).on(MicroSIP.constants.CALL_STATUS.RING, function(call: Call) {
            me.callStatusListener(call);
        }).on(MicroSIP.constants.CALL_STATUS.ESTABLISHED, function(call: Call) {
            me.callStatusListener(call);
        }).on(MicroSIP.constants.CALL_STATUS.HOLD, function(call: Call) {
            me.callStatusListener(call);
        }).on(MicroSIP.constants.CALL_STATUS.FINISH, function(call: Call) {
            me.callStatusListener(call);
        }).on(MicroSIP.constants.CALL_STATUS.FAILED, function(call: Call) {
            me.callStatusListener(call);
        });

        outCall.call();

        this.currentCall = outCall;
    }

    changeVideoState(/* call: Call, enable: boolean */): void {
        trace("Phone - changeVideoState");
        console.warn("It's not implemented yet");
    }

    sendMessage(message: { to: string; body: string; }): void {
        trace("Phone - sendMessage " + message.to + " body: " + message.body);
        console.warn("It's not implemented yet");
    }

    answer(): void {
        if (!this.currentCall) {
            trace("Phone - No current call to answer.");
            return;
        }
        trace("Phone - answer " + this.currentCall.id());
        this.microsipListener?.onAnswer(this.currentCall.id());
        this.currentCall.answer({
            localVideoDisplay: this.localVideo,
            remoteVideoDisplay: this.remoteVideo
        });
    }

    mute(): void {
        if (!this.currentCall) {
            trace("Phone - No current call to answer.");
            return;
        }
        this.currentCall.muteAudio();
        trace("Phone - mute " + this.currentCall);
    }

    unmute(): void {
        if (!this.currentCall) {
            trace("Phone - No current call to answer.");
            return;
        }
        this.currentCall.unmuteAudio();
        trace("Phone - unmute " + this.currentCall);
    }

    hangup(): void {
        if (!this.currentCall) {
            trace("Phone - No current call to hangup.");
            return;
        }
        trace("Phone - hangup " + this.currentCall.id() + " status " + this.currentCall.status());
        this.hideFlashAccess();
        if (this.currentCall.status() == MicroSIP.constants.CALL_STATUS.PENDING) {
            this.callStatusListener(this.currentCall);
        } else {
            this.currentCall.hangup();
        }
        this.microsipListener?.onHangup();
    }

    sendDTMF(dtmf: string): void {
        if (!this.currentCall) {
            trace("Phone - No current call to send DTMF.");
            return;
        }
        trace("Phone - sendDTMF callId: " + this.currentCall.id() + " dtmf: " + dtmf);
        this.currentCall.sendDTMF(dtmf);
        SoundControl.getInstance().playSound("POP");
    }

    hold(): void {
        if (!this.currentCall) {
            trace("Phone - No current call to hold.");
            return;
        }
        trace("Phone - hold callId: " + this.currentCall.id());
        this.currentCall.hold();
    }

    holdForTransfer(): void {
        if (!this.currentCall) {
            trace("Phone - No current call to hold for transfer.");
            return;
        }
        trace("Phone - hold callId: " + this.currentCall.id());
        this.currentCall.holdForTransfer();
    }

    unhold(): void {
        if (!this.currentCall) {
            trace("Phone - No current call to unhold.");
            return;
        }
        trace("Phone - unhold callId: " + this.currentCall.id());
        this.currentCall.unhold();
    }

    transfer(target: string): void {
        if (!this.currentCall) {
            trace("Phone - No current call to transfer.");
            return;
        }
        trace("Phone - transfer callId: " + this.currentCall.id() + " target: " + target);
        this.currentCall.transfer(target);
    }

    getStatistics(): void {
        if (!this.currentCall) {
            trace("Phone - No current call to get statistics.");
            return;
        }
        this.currentCall.getStats(function(statistics: any) {
            trace("Statistics: " + JSON.stringify(statistics));
        });
    }

    hideFlashAccess(): void {

    }

    /* ------------------ LISTENERS ----------------- */

    connectionStatusListener(status: SessionStatus): void {
        trace("Phone - Connection status " + status);
        this.callback.connectionStatus(status);
        const payload = {status}
        this.activities('connectionStatus',{payload});
        
        // this.connectionStatus = status; // Removed unused variable
        if (status == MicroSIP.constants.SESSION_STATUS.DISCONNECTED ||
            status == MicroSIP.constants.SESSION_STATUS.FAILED) {
            if (this.currentCall) {
                this.callStatusListener(this.currentCall); // Pass the call object
            }
            this.currentCall = null;
            this.holdedCall = null;
            this.session = null;
        } else if (status == MicroSIP.constants.SESSION_STATUS.ESTABLISHED) {

        }
    }

    registrationStatusListener(status: SessionStatus): void {
        // this.connectionStatus = status; // Removed unused variable
        trace("Phone - registrationStatusListener " + status);
        this.callback.registrationStatus(status);
        const payload = {status}
        this.activities('registrationStatus',{payload});

        if (status == MicroSIP.constants.SESSION_STATUS.FAILED) {
            this.viewMessage("Register fail, please check your SIP account details.");
            this.disconnect();
        } else if (status == MicroSIP.constants.SESSION_STATUS.UNREGISTERED) {
            this.viewMessage("Unregistered from sip server");
            this.disconnect();
        } else if (status == MicroSIP.constants.SESSION_STATUS.AUTHENTICATION_FAIL) {
            this.viewMessage("Authentication failed, please check your SIP account details.");
            this.disconnect();
        } else {
            SoundControl.getInstance().playSound("REGISTER");
            this.microsipListener?.onRegistered();
        }
    }

    onCallListener(call: Call): void {
        const status = call.status();
        trace("Phone - onCallListener " + call.id() + " call.status: " + call.status());

        this.callback.callStatusListener(status);

        const typeCall  = call.caller() == this.credentials.extension.toString() ? "out" :"in";
        const callNum   = typeCall==="in" ? (call?.caller() ||'') : (call?.callee()||'');

        const payload = {status, callNum:callNum, callId:call.id(), type:typeCall}
        this.activities('callListen',{payload});

        if (this.currentCall != null) {
            this.holdedCall = this.currentCall;
            this.currentCall = call;
            trace("Phone - It seems like a hold: holdedCall: " + this.holdedCall.id() + " currentCall: " + this.currentCall.id());
        } else {
            this.incomingCall = true;
            this.currentCall = call;
            if (this.incomingCall == true) {
                this.microsipListener?.onIncomingCall(call.id());

                //incoming call info
                const status    = "INCOMING_CALL"
                const callNum   = call?.caller() ||'';
                const payload   = {status, callNum:callNum, callId:call.id()}
                this.activities('callListen',{payload});
            }
            trace("Phone - It seems like a new call currentCall: " + this.currentCall.id() + " status: " + this.currentCall.status());

        }
    }

    callStatusListener(call: Call): void {
        const status = call.status();
        trace("Phone - callStatusListener call id: " + call.id() + " status: " + status);

        const typeCall  = call.caller() == this.credentials.extension.toString() ? "out" :"in";
        const callNum   = typeCall==="in" ? (call?.caller() ||'') : (call?.callee()||'');

        this.callback.callStatusListener(status);

        const payload = {status, callNum:callNum, callId:call.id(),type:typeCall}
        this.activities('callStatus',{payload});

        if (this.currentCall && this.currentCall.id() == call.id()) {
            if (status == MicroSIP.constants.CALL_STATUS.FAILED) {
                trace("Phone - ... Call is failed...");
                this.currentCall = null;

            } else if (status == MicroSIP.constants.CALL_STATUS.FINISH || status == MicroSIP.constants.CALL_STATUS.PENDING) {
                trace("Phone - ... Call is finished...");
                SoundControl.getInstance().stopSound("RING");
                if (this.holdedCall != null) {
                    trace("Phone - ... Finish holded call...");
                    this.currentCall = this.holdedCall;
                    this.holdedCall = null;
                } else if (this.isCurrentCall(call)) {
                    trace("Phone - ... Finish current call...");
                    this.currentCall = null;
                    this.microsipListener?.onRemoveCall();
                    SoundControl.getInstance().playSound("FINISH");

                    this.cancel(); // This calls hangup, which might be redundant if the call is already finished.
                    clearInterval(this.timerInterval);
                    this.timerInterval = null;
                }
            } else if (status == MicroSIP.constants.CALL_STATUS.HOLD) {
                trace('Phone - ...Call on hold...');
                if (this.incomingCall) {

                } else {

                }

            } else if (status == MicroSIP.constants.CALL_STATUS.ESTABLISHED) {
                trace('Phone - ...Talking...');

                this.startTimer();

                SoundControl.getInstance().stopSound("RING");
            } else if (status == MicroSIP.constants.CALL_STATUS.RING) {
                trace('Phone - ...Ringing...');
                if (this.isRingSoundAllowed()) {
                    SoundControl.getInstance().playSound("RING");
                }
            } else if (status == MicroSIP.constants.CALL_STATUS.RING_MEDIA) {
                trace('Phone - ...Ringing...');
                SoundControl.getInstance().stopSound("RING");
            }
            else if (status == MicroSIP.constants.CALL_STATUS.BUSY) {
                SoundControl.getInstance().playSound("BUSY");
            } else if (status == MicroSIP.constants.CALL_STATUS.SESSION_PROGRESS) {
                trace('Phone - ...Call in Progress...');
                SoundControl.getInstance().stopSound("RING");
            }
        } else {
            if (this.holdedCall && this.holdedCall.id() == call.id()) {
                if (call.status() == MicroSIP.constants.CALL_STATUS.FINISH) {
                    trace("It seems we received FINISH status on holdedCall. Just do null the holdedCall.");
                    this.holdedCall = null;
                }
            }
        }
    }

    onTransferStatusListener(event: { status: any; incoming: any; }): void {
        trace("Phone - onTransferStatusListener status:" + event.status + " incoming:" + event.incoming);
    }

    onTransferEventListener(event: { status: any; incoming: any; }): void {
        trace("Phone - onTransferEventListener status:" + event.status + " incoming:" + event.incoming);
    }

    parseMsn(fsService: any, mcn: string): void {
        trace("Phone - parseMcn: " + mcn);
        console.log('fsService',fsService);
    }

    recordingStatusListener(recordReport: { report: { mixedFilename: string; }; }): void {
        trace("Phone - recordingStatusListener: " + recordReport.report.mixedFilename);
    }

    onDataEventListener(event: { data: any; }): void {
        trace("Phone - onDataEventListener; received data " + event.data);
    }

    dataStatusEventListener(event: { status: any; }): void {
        trace("Phone - DataStatusEventListener; received status " + event.status);
    }

    viewMessage(message: string): void {
         trace("Message:  " + message);
    }

    openVideoView(): void {
    
    }

    // talk timer
    startTimer(): string {
        const me = this;
        let timeMin = '00';
        let timeSec = '00';
        if (!me.timerInterval) {
            me.start = 0;
            me.min = 0;
            me.timerInterval = setInterval(function () {
                me.start++;
                if (me.start > 59) {
                    me.start = 0;
                    me.min++;
                    if (me.min < 10) {
                        timeMin = "0" + me.min;
                    } else  timeMin = me.min.toString();
                }
                if (me.start < 10) {
                    timeSec = "0" + me.start;
                } else timeSec = me.start.toString();
            }, 1000);
        }
        return `${timeMin}:${timeSec}`;
    }

    /* ------------- Additional interface functions --------- */
    isCurrentCall(call: Call): boolean {
        return this.currentCall != null && this.currentCall.id() == call.id();
    }

    isRingSoundAllowed(): boolean {
        try {
            if (ConfigurationLoader.getInstance().suppressRingOnActiveAudioStream) {
                trace("Phone - isRingSoundAllowed false");
                return false;
            }
        } catch (error) {
            // Handle error if ConfigurationLoader or its properties are undefined
        }
        trace("Phone - isRingSoundAllowed true");
        return true;
    }

    applyCalleeLetterCase(callee: string): string {
        if (callee) {
            if ("uppercase" == ConfigurationLoader.getInstance().calleeLetterCase) {
                return callee.toUpperCase();
            }
            if ("lowercase" == ConfigurationLoader.getInstance().calleeLetterCase) {
                return callee.toLowerCase();
            }
        }
        return callee;
    }
}

export default Phoner;
